import { useState, useEffect, useCallback } from 'react';
import { announcementService } from 'services/api';
import styles from './ComunicadosPanel.module.css';

const fmtFecha = (d) => d
  ? new Date(d).toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' })
  : '';

// Hook: comunicados del propietario (publicados de sus conjuntos) + unread.
// propertyId opcional → filtra a un solo conjunto.
export function useComunicados(propertyId) {
  const [items, setItems]   = useState(null);
  const [unread, setUnread] = useState(0);

  const reload = useCallback(async () => {
    try {
      const r = await announcementService.getAll(propertyId ? { property_id: propertyId } : undefined);
      setItems(r.data?.announcements || []);
      setUnread(r.data?.unread || 0);
    } catch { setItems([]); }
  }, [propertyId]);

  useEffect(() => { reload(); }, [reload]);
  return { items, unread, reload };
}

// Marca leído (persistente) + avisa a la campana.
async function marcarLeido(id) {
  try {
    await announcementService.markRead(id);
    window.dispatchEvent(new Event('announcements:read'));
    return true;
  } catch { return false; }
}

// Un comunicado. Muestra de qué conjunto / apartamento(s) es y si es para tener
// en cuenta en una asamblea o es general.
function ComunicadoCard({ a, expanded, onToggle, unreadVisual }) {
  const paraAsamblea = !!a.assembly_number;
  return (
    <div className={unreadVisual ? styles.tarjetaSinLeer : styles.tarjeta}>
      <button onClick={onToggle}
        className={styles.cabecera}>
        {unreadVisual
          ? <span className={styles.punto} />
          : <i className={`fas fa-bullhorn ${styles.altavoz}`} />}
        <div className={styles.textos}>
          <div className={styles.filaTitulo}>
            <span className={unreadVisual ? (expanded ? styles.tituloSinLeerAbierto : styles.tituloSinLeer) : (expanded ? styles.tituloAbierto : styles.titulo)}>
              {a.title}
            </span>
            <span className={paraAsamblea ? styles.chipAsamblea : styles.chipGeneral}>
              {paraAsamblea ? `Para la asamblea #${a.assembly_number}` : 'General'}
            </span>
          </div>
          <div className={styles.meta}>
            {a.property_name || 'Tu conjunto'}
            {a.units && a.units.length
              ? ` · ${a.units.length === 1 ? 'Apto' : 'Aptos'} ${a.units.join(', ')}`
              : ''}
            {' · '}{fmtFecha(a.published_at)}
          </div>
        </div>
        <i className={`fas fa-chevron-${expanded ? 'up' : 'down'} ${styles.flecha}`} />
      </button>
      {expanded && (
        <div className={styles.cuerpo}>
          {a.body}
        </div>
      )}
    </div>
  );
}

// Lista de comunicados. onlyUnread=true → oculta los ya leídos (salvo el que se
// acaba de leer en esta sesión, para poder terminar de leerlo). Al expandir uno
// sin leer, se marca como leído.
export function ComunicadosList({ items, onlyUnread = false, emptyText, onChange }) {
  const [open, setOpen] = useState(null);
  const [sessionRead, setSessionRead] = useState(() => new Set());

  const toggle = async (a) => {
    const abrir = open !== a.id;
    setOpen(abrir ? a.id : null);
    if (abrir && !a.read && !sessionRead.has(a.id)) {
      setSessionRead(s => new Set(s).add(a.id));
      await marcarLeido(a.id);
      onChange?.();
    }
  };

  if (items === null)
    return <div className={styles.cargando}><div className={`spinner ${styles.spinnerCarga}`} /></div>;

  const visibles = onlyUnread
    ? items.filter(a => !a.read || sessionRead.has(a.id))
    : items;

  if (!visibles.length)
    return (
      <div className={styles.vacio}>
        <i className={`fas fa-inbox ${styles.iconoVacio}`} />
        {emptyText || 'No hay comunicados de la administración por ahora.'}
      </div>
    );

  return (
    <div className={styles.lista}>
      {visibles.map(a => (
        <ComunicadoCard key={a.id} a={a} expanded={open === a.id}
          unreadVisual={!a.read && !sessionRead.has(a.id)}
          onToggle={() => toggle(a)} />
      ))}
    </div>
  );
}

// Modal "Historial de comunicados" — TODOS (leídos y no leídos). Usado desde
// "Mis unidades". propertyId opcional → historial de un solo conjunto.
export function ComunicadosHistorialModal({ onClose, propertyId, propertyName }) {
  const { items } = useComunicados(propertyId);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal ${styles.modalHistorial}`} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3><i className={`fas fa-bullhorn ${styles.iconoTitulo}`} />
            Historial de comunicados</h3>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p className={styles.intro}>
            {propertyId
              ? <>Comunicados de la administración del conjunto <strong>{propertyName || ''}</strong>.</>
              : 'Todos los comunicados de la administración de tus conjuntos.'}
          </p>
          <ComunicadosList items={items} emptyText="Aún no has recibido comunicados." />
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}
